import React from 'react';
import { motion } from 'framer-motion';
import { 
  ChartBarIcon, 
  CogIcon, 
  LightBulbIcon,
  CloudArrowUpIcon,
  ShieldCheckIcon,
  ChartPieIcon 
} from '@heroicons/react/24/outline';
import { useContext } from 'react';
import { LanguageContext } from '../contexts/LanguageContext';
import { translations } from '../locales/translations';

const Home = () => {
  const { language } = useContext(LanguageContext);
  const t = translations[language];

  const products = [
    {
      title: t.products.title,
      description: t.products.subtitle,
      icon: ChartPieIcon,
      color: "bg-blue-500",
    },
    {
      title: t.products.title,
      description: t.products.subtitle,
      icon: ChartBarIcon,
      color: "bg-purple-500",
    },
    {
      title: t.products.title,
      description: t.products.subtitle,
      icon: CogIcon,
      color: "bg-green-500",
    }
  ];

  const features = [
    {
      title: t.features.title,
      description: t.features.subtitle,
      icon: CloudArrowUpIcon,
    },
    {
      title: t.features.title,
      description: t.features.subtitle,
      icon: LightBulbIcon,
    },
    {
      title: t.features.title,
      description: t.features.subtitle,
      icon: ShieldCheckIcon,
    }
  ];

  // 更新案例数据，根据语言选择不同内容
  const cases = language === 'en' ? [
    {
      logo: "Digital Accessories Brand",
      title: "Amazon Global Store Optimization",
      description: "Helped optimize operations across 5 country sites, guided product selection and pricing through data analysis, and optimized PPC advertising.",
      results: ["Monthly sales increased by 45%", "Ad ACOS reduced by 30%", "Profit margin improved by 15%"],
    },
    {
      logo: "Beauty Brand",
      title: "TikTok Shop Performance Enhancement",
      description: "Optimized livestream scheduling and product planning through data analysis, improved conversion rates, and analyzed user behavior data to enhance product presentation.",
      results: ["Livestream conversion up by 60%", "Average order value up by 25%", "Follower growth rate 200%"],
    },
    {
      logo: "Home Furnishing Brand",
      title: "Cross-platform Data Integration",
      description: "Integrated Amazon and TikTok Shop sales data, unified inventory and order management to prevent overselling and optimize supply chain.",
      results: ["Inventory turnover up by 40%", "Stock-out rate down by 80%", "Operational efficiency up by 50%"],
    }
  ] : [
    {
      logo: "某数码配件品牌",
      title: "亚马逊全球开店优化",
      description: "帮助卖家优化5个国家站点的运营策略，通过数据分析指导选品和定价，优化PPC广告投放。",
      results: ["月均销售额提升45%", "广告ACOS降低30%", "利润率提升15%"],
    },
    {
      logo: "某美妆品牌",
      title: "TikTok Shop运营提升",
      description: "通过直播数据分析，优化直播场次和商品排期，提升转化率。分析用户行为数据，优化产品展示。",
      results: ["直播转化率提升60%", "平均客单价提升25%", "粉丝增长率200%"],
    },
    {
      logo: "某家居品牌",
      title: "跨平台数据整合",
      description: "整合亚马逊和TikTok Shop的销售数据，统一管理库存和订单，避免超卖，优化供应链。",
      results: ["库存周转率提升40%", "缺货率降低80%", "运营效率提升50%"],
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="hero-gradient min-h-screen flex items-center">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-5xl md:text-7xl font-bold text-white mb-6">
              {t.hero.title}
              <br />
              {t.hero.subtitle}
            </h1>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
              {t.hero.description.split('\n').map((line, i) => (
                <React.Fragment key={i}>
                  {line}
                  <br />
                </React.Fragment>
              ))}
            </p>
            <div className="flex justify-center gap-4">
              <button className="bg-white text-primary px-8 py-3 rounded-lg font-medium hover:bg-gray-100 transition-colors">
                {t.hero.getStarted}
              </button>
              <button className="border-2 border-white text-white px-8 py-3 rounded-lg font-medium hover:bg-white/10 transition-colors">
                {t.hero.watchDemo}
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Products Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              {t.products.title}
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              {t.products.subtitle}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {products.map((product, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.2 }}
                viewport={{ once: true }}
                className="bg-white rounded-2xl shadow-xl p-8 hover:shadow-2xl transition-shadow"
              >
                <div className={`${product.color} w-12 h-12 rounded-lg flex items-center justify-center mb-6`}>
                  <product.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{product.title}</h3>
                <p className="text-gray-600">{product.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              {t.features.title}
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              {t.features.subtitle}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-12">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.2 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="bg-primary/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                  <feature.icon className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Cases Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              {t.cases.title}
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              {t.cases.subtitle}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {cases.map((case_, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.2 }}
                viewport={{ once: true }}
                className="bg-white rounded-2xl shadow-xl p-8 hover:shadow-2xl transition-shadow"
              >
                <div className="text-lg font-semibold text-primary mb-4">
                  {case_.logo}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  {case_.title}
                </h3>
                <p className="text-gray-600 mb-6">
                  {case_.description}
                </p>
                <div className="space-y-2">
                  {case_.results.map((result, idx) => (
                    <div key={idx} className="flex items-center text-sm text-gray-600">
                      <div className="w-2 h-2 bg-primary rounded-full mr-2"></div>
                      {result}
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>

          {/* 合作品牌展示 */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="mt-20 text-center"
          >
            <p className="text-gray-500 mb-8">{t.cases.more}</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {t.cases.industries.map((industry, index) => (
                <div key={index} className="text-gray-400 font-medium">{industry}</div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;
